import { Injectable, NgZone } from '@angular/core';
import { IDiscoverService } from "@discover/interfaces/i-discover-service";
import { LayoutState, ViewStateRecordsAlias } from "@common/services/layout-state";
import { DataStore } from "@suite/common/data-models/data-store/data-store";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { AppService } from "@suite/app.service";
import { SingleViewStateRecord } from "@common/data-models/records/single-viewstate-record";
import { Subscription } from "rxjs";
import { DiscoverSearchService } from "@discover/services/discover-search-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { NavigationService } from "@common/services/navigation-service";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { ViewStateTypes } from "@common/enums/viewstate-types";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { LanguageSettings } from "@common/services/language-settings";
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ViewStateLayouts } from "@common/enums/viewstate-layouts";
import { DiscoverSearchResults } from "@discover/data-models/discover-search-results/discover-search-results";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { FieldDef } from "@common/data-models/service/field-def";
import { Router } from '@angular/router';
import { PlaybookService } from '@suite/playbook/services/playbook-service';
import { BasicContentService } from '@suite/common/services/basic-content-service';
import { ObjectTypes } from '@common/enums/object-types';
import { GroupValueHelpers, GroupKeyComparatorHelpers, RecordComparatorHelpers, ResultGroupingDefinition, ResultGrouping2Definition } from "@common/data-models/search-results/result-group";

@Injectable({ providedIn: 'root' })
export class DiscoverService implements IDiscoverService {

    private viewStateRecordSubscription: Subscription;
    private viewStateRecordPropertySubscription: Subscription;

    public WireRequiredFieldDefs: Array<FieldDef> = [];
    public WireRequiredFieldDefsDict: { [id: string]: FieldDef } = {};

    public groupByWires: Array<{ id: string, title: string }> = [];
    public sortByWires: Array<{ id: string, title: string }> = [];

    constructor(
        public appService: AppService,
        public discoverSearchService: DiscoverSearchService,
        public viewStateService: LayoutState,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public nomWebServicesJson: NomWebServicesJson,
        public ngZone: NgZone,
        public navigationService: NavigationService,
        private router: Router,
        public playbookService: PlaybookService,
        public basicContentService: BasicContentService,
    ) {
        DiscoverSearchResults.discoverService = this;

        this.globalTablesService.calculateDefaultGroupSort["discover"] = this.fillGroupSortIfEmpty.bind(this);

        this.globalTablesService.staticPropertyHandlers["WIRE"] = this.getWireStaticProperties.bind(this);

        this.globalTablesService.isPropertyForType[ObjectTypes.WIRE] = this.isWireProperty.bind(this);

        this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.WIRE] = this.populateDefaultValuesForWire.bind(this);

        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.WIRE] = this.setFieldDefsForWires.bind(this);

        this.globalTablesService.getGroupingOptions[ViewStateTypes.WIRE] = this.getGroupingOptionsForWires.bind(this);
        this.globalTablesService.getGroupingOptions[ViewStateTypes.WIRESEARCH] = this.getGroupingOptionsForWires.bind(this);

        this.globalTablesService.getSortingOptions[ViewStateTypes.WIRE] = this.getSortingOptionsForWires.bind(this);
        this.globalTablesService.getSortingOptions[ViewStateTypes.WIRESEARCH] = this.getSortingOptionsForWires.bind(this);

        this.globalTablesService.getLayoutOptions[ViewStateTypes.WIRE] = this.getLayoutOptionsForWires.bind(this);
        this.globalTablesService.getLayoutOptions[ViewStateTypes.WIRESEARCH] = this.getLayoutOptionsForWires.bind(this);

        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.WIRE] = this.getFiltersForWires().bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.WIRESEARCH] = this.getFiltersForWires().bind(this);

        this.globalTablesService.clearServices["discover"] = this.clearDiscoverService.bind(this);
    }

    public WireBaseRequiredFields: Array<string> = [
        PSTFieldNames.PSTTopicSlug,
        PSTFieldNames.WireCategory,
        PSTFieldNames.WireProvider
    ];

    public getWireStaticProperties(): { [id: string]: boolean } {
        let ret: { [id: string]: boolean } = {};
        ret[PSTFieldNames.PSTWireCategory] = true;
        ret[PSTFieldNames.Creator] = true;
        ret[PSTFieldNames.PSTWireTitle] = true;
        ret[PSTFieldNames.PSTWireText] = true;
        ret[PSTFieldNames.PSTWireImage] = true;
        ret[PSTFieldNames.PSTWireProvider] = true;
        return ret;
    }

    public isWireProperty(fieldDef: FieldDef): boolean {
        return fieldDef.WireProp;
    }

    public getSortingOptionsForWires(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTModTime;
        ret.Options = this.sortByWires;
        return ret;
    }

    private _groupByWireKeys: Array<string> = [];
    public get groupByWireKeys(): Array<string> {
        if ((this._groupByWireKeys.length == 0) && (this.groupByWires.length > 0)) {
            this._groupByWireKeys = this.groupByWires.map(item => item.id);
        }
        return this._groupByWireKeys;
    }

    public getGroupingOptionsForWires(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.Creator;
        ret.Options = this.groupByWires;
        return ret;
    }

    public getLayoutOptionsForWires(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = ViewStateLayouts.TILE;
        ret.Options = [
            { id: ViewStateLayouts.TILE, title: this.languageSettings.PSTWebViewTimelineLabel },
            { id: ViewStateLayouts.MAP, title: this.languageSettings.PSTWebViewMapLabel },
            { id: ViewStateLayouts.TRENDS, title: this.languageSettings.PSTWebViewTrendsLabel }
        ];
        return ret;
    }

    public getFiltersForWires(): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            if (item.Briefable && (item["WireProp"] == true)) {
                return true;
            }
            return false;
        }
    }
    
    public activeCollectionPollerStarted: boolean = false;

    public get discoverSearchResults(): DiscoverSearchResults {
        return DataStore.getSearchResults<DiscoverSearchResults>("discover");
    }

    public async loadWires(vs?: ViewStateRecordsAlias, guid?: string) {

        if (!vs) vs = this.viewStateService.ViewStateRecord;
        DiscoverSearchResults.viewState = <SingleViewStateRecord>vs;
        if (this.navigationService.currentListView) this.navigationService.currentListView.showLoader();
        await this.appService.ngZone.runOutsideAngular(async () => {
            DataStore.disposeSearchResults();
            // SearchResults.newlyAddedItems.dispose();

            let res: ServiceResponse<DiscoverSearchResults> = await this.discoverSearchService.getSearchResults(vs, guid);
            
            if (!this.activeCollectionPollerStarted) {
                this.activeCollectionPollerStarted = true;
                this.startActiveCollectionPoller();
            }

            if (res.status) {
                DataStore.registerSearchResults("discover", res.data);
                this.discoverSearchResults.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<SingleViewStateRecord>vs).viewStateSortOrder, (<SingleViewStateRecord>vs).viewStateGroupSortOrder);
            } else {
                // Error
            }
            this.appService.LoadingDataSource.next(false);
            this.invalidateVisualState();
        });
    }

    public invalidateVisualState() {
        this.appService.timeoutQueue.setTimeout(async () => {
            this.appService.ngZone.run(() => {
                this.navigationService.currentListChangeDetectorRef.detectChanges();
            });
            if (!this.navigationService.CurrentNavigationItem || !this.navigationService.CurrentNavigationItem.isNewView()) {
                this.navigationService.currentListView.hideLoader();
            }

            this.appService.ngZone.run(() => {
                this.navigationService.currentListChangeDetectorRef.detectChanges();
            });

            if (this.navigationService.CurrentNavigationItem && this.navigationService.CurrentNavigationItem.isNewView()) {
                await this.navigationService.saveCurrentNavigationItem();
                this.navigationService.currentListView.hideLoaderDirectly();
            }
        }, 0);
    }

    public startActiveCollectionPoller() {
        this.ngZone.runOutsideAngular(async () => {
            setInterval(async () => {
                if (!this.nomWebServicesJson.noSession) {
                    let vs: ViewStateRecordsAlias = this.viewStateService.ViewStateRecord;
                    if (vs instanceof SingleViewStateRecord) {
                        let res: ServiceResponse<DiscoverSearchResults> = await this.discoverSearchService.getSearchResults(vs, null, true);
                        if (res && res.status) {
                            let sr: DiscoverSearchResults = res.data;
                            sr.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<SingleViewStateRecord>vs).viewStateSortOrder, (<SingleViewStateRecord>vs).viewStateGroupSortOrder);
                            (<DiscoverSearchResults>DataStore.getSearchResults('discover')).mergeSearchResults(sr);
                            this.invalidateVisualState();
                        } else {
                            this.nomWebServicesJson.noSession = true;
                        }
                    }
                }
            }, this.appService.getSolrPollInvervalInSeconds());
        });
    }
    public populateDefaultValuesForWire(f: FieldDef, criteriaObj: any) {
        if (f.WireProp) {
            if (!this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.WIRE]) {
                this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.WIRE] = {};
            }
            this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.WIRE][f.ID] = criteriaObj;
        }
    }

    public setFieldDefsForWires(fieldDef: FieldDef) {
        let validation = fieldDef.Validation.toUpperCase();
        if (fieldDef.Required) {
            if (validation == "CHECKBOX"
                || (validation == "LOOKUPLIST" && fieldDef.ID != PSTFieldNames.PSTTopicTag)
                || !this.globalTablesService.isValidationTypeImplemented(validation)
            ) {
                // Required could not be applied to checkbox and unimplemented validation types
                return;
            }
            if (fieldDef.TopicProp || this.WireBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                this.WireRequiredFieldDefs.push(fieldDef);
                this.WireRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
            }
        }
        if (fieldDef.WireProp) {
            this.calculateWirePropComparators(fieldDef)
        }
    }

    private calculateWirePropComparators(prop: FieldDef) {
        let shouldAdd: boolean = true;
        switch (prop.Validation.toLowerCase()) {
            case 'staffidlist': {
                DiscoverSearchResults.wireGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.StaffOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.StaffOrDefault(prop.ID, "No value")
                );
                break;
            }
            case 'text':
            case 'combobox':
            case 'tree': {
                DiscoverSearchResults.wireGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.StringOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.Generic(prop.ID)
                );
                break;
            }
            case 'checklistedit':
            case 'checklist': {
                DiscoverSearchResults.wireGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.MultiValueOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.MultiValue(prop.ID)
                );
                break;
            }
            case 'date': {
                DiscoverSearchResults.wireGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.DateTime(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.DateTime(prop.ID)
                );
                break;
            }
            case 'checkbox': {
                DiscoverSearchResults.wireGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.Boolean(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.Boolean(prop.ID)
                );
                break;
            }
            default:
                shouldAdd = false;
        }
        if (prop.GroupableProp && shouldAdd) {
            this.groupByWires.push({ id: prop.ID, title: prop.DefaultCaption });
        }
        if (prop.SortableProp && shouldAdd) {
            this.sortByWires.push({ id: prop.ID, title: prop.DefaultCaption });
        }
    }

    private fillGroupSortIfEmpty() {
        DiscoverSearchResults.wireGroupDefs[0] = new ResultGroupingDefinition(
            GroupValueHelpers.StringOrDefaultWire('RSS Feed', "None"),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("None"),
            RecordComparatorHelpers.Generic('0')
        );
        this.groupByWires.push({ id: 'RSS Feed', title: 'RSS Feed' });

        DiscoverSearchResults.wireGroupDefs[1] = new ResultGroupingDefinition(
            GroupValueHelpers.StringOrDefaultWire('AP Feed', "None"),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("None"),
            RecordComparatorHelpers.Generic('1')
        );
        // DiscoverSearchResults.wireGroupDefs['ApWire'] = new ResultGroupingDefinition(
        //     GroupValueHelpers.StringOrDefaultWire('AP Feed', "None"),
        //     GroupKeyComparatorHelpers.DisplayValueDefaultBottom("None"),
        //     RecordComparatorHelpers.Generic('ApWire')
        // );
        this.groupByWires.push({ id: 'AP Feed', title: 'AP Feed' });

        DiscoverSearchResults.wireGroupDefs[2] = new ResultGroupingDefinition(
            GroupValueHelpers.StringOrTweetWire('userName', "None"),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("None"),
            RecordComparatorHelpers.Generic('2')
        );
        this.groupByWires.push({ id: 'Twitter Feed', title: 'Twitter Feed' });

        // DiscoverSearchResults.wireGroupDefs['TwitterWire'] = new ResultGroupingDefinition(
        //     GroupValueHelpers.StringOrTweetWire('Twitter Feed', "None"),
        //     GroupKeyComparatorHelpers.DisplayValueDefaultBottom("None"),
        //     RecordComparatorHelpers.Generic('TwitterWire')
        // );
        // this.groupByWires.push({ id: 'Twitter Feed', title: 'Twitter Feed' });

        DiscoverSearchResults.wireGroupDefs[PSTFieldNames.PSTModTime] = new ResultGroupingDefinition(
            GroupValueHelpers.DateTime(PSTFieldNames.PSTModTime),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTModTime)
        );
        if (this.sortByWires && this.sortByWires.length == 0) {
            this.sortByWires = [
                { id: PSTFieldNames.PSTModTime, title: this.languageSettings.PSTWebSortByModTimeLabel }
            ];
        }
    }

    public clearDiscoverService() {
        this.groupByWires = [];
        this.sortByWires = [];
    }
}
